import React, { useState } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import Icon from 'components/styleguide/icon/Icon';
import { saveOverrides } from './utils/curationApi';
import {
  AiSkill,
  displayPct,
  EffectiveScores,
  MUST_HAVE_WEIGHT,
  NICE_TO_HAVE_WEIGHT,
  SkillState,
} from './utils/curationScoring';
import { useCurationReview } from './state/CurationReviewContext';
import OverrideBox from './OverrideBox';
import SkillToggle from './SkillToggle';

const styles = require('./SkillsBreakdown.module.scss');

export interface Props {
  scores: EffectiveScores;
}

const LEGEND: { label: string; state: SkillState }[] = [
  { label: 'Explicit in CV', state: 'explicit' },
  { label: 'Inferred by AI', state: 'inferred' },
  { label: 'Not found', state: 'not_found' },
];

// Must have and Nice to have only — no further subdivisions. Every skill
// pill is clickable; scores recalculate on the spot and the override
// persists in the background.
export default function SkillsBreakdown({ scores }: Props) {
  const { dispatch, state, urls } = useCurationReview();
  const [saveError, setSaveError] = useState(false);

  const skills = state.payload ? state.payload.skills : [];
  const stateFor = (skill: AiSkill): SkillState => state.skillStates[skill.name] || skill.state;

  const onToggle = async (name: string, next: SkillState) => {
    const base = skills.find((skill) => skill.name === name);
    const previous = state.skillStates[name];
    dispatch({ from: base ? base.state : next, name, type: 'TOGGLE_SKILL' });
    setSaveError(false);
    try {
      await saveOverrides(urls, {
        skill_overrides: { [name]: base && base.state === next ? null : next },
      });
    } catch {
      // Roll the pill back so the displayed score cannot claim an override the
      // server never stored.
      dispatch({ name, state: previous, type: 'RESET_SKILL' });
      setSaveError(true);
    }
  };

  const group = (title: string, groupName: AiSkill['group'], average: number | null, weight: number) => {
    const groupSkills = skills.filter((skill) => skill.group === groupName);
    if (groupSkills.length === 0) return null;

    return (
      <div className={styles['ui-skills__group']}>
        <h4 className={styles['ui-skills__group-title']}>
          {title}
          <span className={styles['ui-skills__group-score']}>
            {` · ${displayPct(average)} · weight ${Math.round(weight * 100)}%`}
          </span>
        </h4>
        <div className={styles['ui-skills__tags']}>
          {groupSkills.map((skill) => (
            <SkillToggle
              disabled={state.submitted}
              evidence={skill.evidence}
              key={skill.name}
              name={skill.name}
              onChange={onToggle}
              state={stateFor(skill)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles['ui-skills']}>
      <div className={styles['ui-skills__header']}>
        <h3 className={styles['ui-skills__heading']}>Skills breakdown</h3>
        <span className={styles['ui-skills__score']}>{displayPct(scores.skills)}</span>
      </div>

      <ul aria-hidden className={styles['ui-skills__legend']}>
        {LEGEND.map((entry) => (
          <li className={styles[`ui-skills__legend--${entry.state}`]} key={entry.state}>
            {entry.label}
          </li>
        ))}
      </ul>

      {saveError && <Alert message="A change couldn't be saved. Try again." variant="error" />}

      {group('Must have', 'must_have', scores.mustHaveAvg, MUST_HAVE_WEIGHT)}
      {group('Nice to have', 'nice_to_have', scores.niceToHaveAvg, NICE_TO_HAVE_WEIGHT)}

      <p className={styles['ui-skills__note']}>
        <Icon aria-hidden name="info" size="sm" />
        Inferred skills come from context in the CV. Click any skill to reclassify it. Scores
        recalculate as you change them.
      </p>

      <OverrideBox
        description={
          <>
            AI score: <strong>{displayPct(scores.computedSkills)}</strong>. Adjust if you disagree
            with the analysis above.
          </>
        }
        fields={[{ key: 'skills_pct', label: 'Skills %' }]}
        title="Override skills score"
      />
    </div>
  );
}
